<template>
  <div class="complex-filter-source-group">
    <div v-if="title || dataSource" class="complex-filter-source-group__header">
      <DataSourceAvatar
        v-if="dataSource"
        :data-source="dataSource"
        size="x-small"
      />
      <span v-if="title" class="complex-filter-source-group__title">
        {{ title }}
      </span>
    </div>

    <div class="complex-filter-source-group__primary">
      <ComplexFilterNestedGroup
        variant="root"
        :hide-add-filter="true"
        :filter-ids="primaryGroupIds"
        :filters="filters"
        :field-options="fieldOptions"
        :used-field-values="usedFieldValues"
        :disabled="disabled"
        :filter-mode="filterMode"
        @update:filters="(f) => emitUpdate(f, expression)"
        @update:filter-ids="(ids) => updateGroupIds(0, ids)"
      />
    </div>

    <div
      v-for="(groupIds, i) in nestedGroups"
      :key="getGroupKey(i)"
      class="complex-filter-source-group__or-row"
    >
      <div class="or-connector">
        <div class="or-line top" />
        <FilterCombinator text="Or" />
        <div class="or-line bottom" />
      </div>
      <ComplexFilterNestedGroup
        variant="nested"
        class="flex-1 min-w-0"
        :filter-ids="groupIds"
        :filters="filters"
        :field-options="fieldOptions"
        :used-field-values="usedFieldValues"
        :disabled="disabled"
        :filter-mode="filterMode"
        @update:filters="(f) => emitUpdate(f, expression)"
        @update:filter-ids="(ids) => updateGroupIds(i + 1, ids)"
        @delete="deleteGroup(i + 1)"
      />
    </div>

    <div
      v-if="!disabled && filterMode !== 'readOnly'"
      class="flex items-center justify-between"
    >
      <div class="complex-filter-source-group__actions">
        <BittsButton
          v-if="hasRemainingFields"
          type="neutral"
          variant="ghost"
          text="Add filter"
          size="small"
          :left-icon="['far', 'plus']"
          @click="addFilterToGroup(0)"
        />

        <BittsButton
          v-if="allowNesting && !hasLockedRow"
          type="neutral"
          variant="ghost"
          text="Add group"
          size="small"
          :left-icon="['far', 'plus']"
          :disabled="hasIncompleteFilter"
          @click="addNewGroup"
        />
      </div>

      <BittsButton
        v-if="!hasRequiredFilter && !hasLockedRow"
        type="neutral"
        variant="ghost"
        text="Delete object"
        size="small"
        @click="$emit('delete:table')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsButton } from '@crossbeam/bitts';

import { computed, inject, ref } from 'vue';

import DataSourceAvatar from '@/components/DataSourceAvatar.vue';

import { ProvidedUserAddedFilterLabels } from '@/injectionKeys/lists';
import type { FilterFieldOption } from '@/types/filters';

import ComplexFilterNestedGroup, {
  type NestedGroupFilter,
} from './ComplexFilterNestedGroup.vue';
import FilterCombinator from './FilterCombinator.vue';
import {
  type DataSourceType,
  type FilterMode,
  NO_OPERAND_OPERATORS,
} from './types';

const props = withDefaults(
  defineProps<{
    title?: string;
    dataSource?: DataSourceType;
    filters: Map<string, NestedGroupFilter>;
    expression: string[];
    fieldOptions: FilterFieldOption<string | number>[];
    allowNesting?: boolean;
    excludeSelectedFields?: boolean;
    disabled?: boolean;
    filterMode?: FilterMode;
  }>(),
  {
    allowNesting: true,
    excludeSelectedFields: false,
    title: '',
    dataSource: undefined,
    disabled: false,
    filterMode: 'editable',
  },
);

const emit = defineEmits<{
  'update:state': [state: FilterGroupState];
  'delete:table': [];
}>();

const userAddedFilterLabels = inject(
  ProvidedUserAddedFilterLabels,
  ref(new Set<string>()),
);

export type FilterGroupState = {
  filters: Map<string, NestedGroupFilter>;
  expression: string[];
};

const hasRequiredFilter = computed(() =>
  Array.from(props.filters.values()).some((filter) => {
    if (filter.value === undefined) return false;
    return props.fieldOptions.some(
      (f) => f.value === filter.value && f.canDelete === false,
    );
  }),
);

const usedFieldValues = computed(() => {
  if (!props.excludeSelectedFields) return undefined;
  const values = new Set<string | number>();
  for (const filter of props.filters.values()) {
    if (filter.value !== undefined) {
      values.add(filter.value);
    }
  }
  return values;
});

const hasRemainingFields = computed(() => {
  if (!props.excludeSelectedFields) return true;
  return props.fieldOptions.some((f) => !usedFieldValues.value?.has(f.value));
});

function emitUpdate(
  filters: Map<string, NestedGroupFilter>,
  expression: string[],
) {
  emit('update:state', { filters, expression });
}

/**
 * Parse expression into groups.
 * ["uuid1", "AND", "uuid2", "OR", "uuid3", "AND", "uuid4"]
 * -> [[uuid1, uuid2], [uuid3, uuid4]]
 */
const parsedGroups = computed(() => {
  const groups: string[][] = [];
  let currentGroup: string[] = [];

  for (const token of props.expression) {
    if (token === 'OR') {
      if (currentGroup.length > 0) {
        groups.push(currentGroup);
        currentGroup = [];
      }
    } else if (token !== 'AND') {
      currentGroup.push(token);
    }
  }

  if (currentGroup.length > 0 || groups.length === 0) {
    groups.push(currentGroup);
  }

  return groups;
});

const primaryGroupIds = computed(() => parsedGroups.value[0] ?? []);
const nestedGroups = computed(() => parsedGroups.value.slice(1));

const hasLockedRow = computed(
  () =>
    props.filterMode === 'hybrid' &&
    Array.from(props.filters.values()).some((f) => f.isLocked === true),
);

// Check if any filter row is incomplete (no operand value provided)
const hasIncompleteFilter = computed(() =>
  Array.from(props.filters.values()).some((filter) => {
    // "is empty" / "is not empty" don't need an operand
    if (filter.operator && NO_OPERAND_OPERATORS.has(filter.operator)) {
      return false;
    }

    const op = filter.operand;
    // No operand set yet, or null, or empty array
    return op === undefined || op === null || (Array.isArray(op) && !op.length);
  }),
);

const getGroupKey = (nestedIndex: number) => {
  const actualIndex = nestedIndex + 1;
  const group = parsedGroups.value[actualIndex];
  return group && group.length > 0 ? group[0] : `empty-group-${actualIndex}`;
};

function buildExpression(groups: string[][]): string[] {
  // Filter out empty groups first
  const nonEmpty = groups.filter((g) => g.length > 0);
  return nonEmpty.flatMap((group, i) => {
    const groupExpr = group.flatMap((id, j) => (j > 0 ? ['AND', id] : [id]));
    return i > 0 ? ['OR', ...groupExpr] : groupExpr;
  });
}

// Update IDs for a specific group index
// Also removes any filters no longer referenced in the expression
function updateGroupIds(index: number, ids: string[]) {
  const groups = [...parsedGroups.value];
  groups[index] = ids;

  // Get all IDs still in the expression
  const allIds = new Set(groups.flat());

  // If no filters remain, remove the entire group
  if (allIds.size === 0) {
    emit('delete:table');
    return;
  }

  // Remove filters not in expression
  const newFilters = new Map(props.filters);
  for (const id of newFilters.keys()) {
    if (!allIds.has(id)) {
      newFilters.delete(id);
    }
  }

  emitUpdate(newFilters, buildExpression(groups));
}

function addFilterToGroup(index: number) {
  const newId = crypto.randomUUID();

  const newFilters = new Map(props.filters);
  newFilters.set(newId, { label: newId });
  if (props.filterMode === 'hybrid') userAddedFilterLabels.value.add(newId);

  const groups = [...parsedGroups.value];
  const targetGroup = groups[index] || [];
  groups[index] = [...targetGroup, newId];

  emitUpdate(newFilters, buildExpression(groups));
}

function addNewGroup() {
  const newId = crypto.randomUUID();
  const newFilters = new Map(props.filters);
  newFilters.set(newId, { label: newId });
  if (props.filterMode === 'hybrid') userAddedFilterLabels.value.add(newId);

  const groups = [...parsedGroups.value];
  groups.push([newId]);

  emitUpdate(newFilters, buildExpression(groups));
}

function deleteGroup(index: number) {
  const groups = [...parsedGroups.value];
  const groupToDelete = groups[index];

  const newFilters = new Map(props.filters);
  if (groupToDelete) {
    groupToDelete.forEach((id) => newFilters.delete(id));
  }

  groups.splice(index, 1);

  emitUpdate(newFilters, buildExpression(groups));
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";

.complex-filter-source-group {
  @apply flex flex-col gap-12 bg-neutral-background-weak rounded-lg p-12;
}

.complex-filter-source-group__header {
  @apply flex items-center gap-8;
}

.complex-filter-source-group__title {
  @apply text-neutral-text-weak font-bold;
}

.complex-filter-source-group__primary {
  @apply flex flex-col gap-8;
}

.complex-filter-source-group__or-row {
  @apply flex items-stretch gap-8 min-w-0;
}

.or-connector {
  @apply flex flex-col items-center w-72 shrink-0;
}

.or-line {
  @apply flex-1 border-neutral-border min-h-12 w-1/2 ml-[50%];
  transform: translateX(-1px);

  &.top {
    @apply border-l border-t rounded-tl-8;
  }

  &.bottom {
    @apply border-l border-b rounded-bl-8;
  }
}

.complex-filter-source-group__actions {
  @apply flex gap-4;
}
</style>
